import React, { useEffect, useMemo, useState } from "react";
import { ArrowLeft, Package2, X } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { warehouseCatalogService } from "../../../services/warehouseCatalog.service";

const BORDER = "#f2c6cc";

function valueOrDash(value) {
  return value === null || value === undefined || value === "" ? "-" : value;
}

function formatCurrency(value, currency = "INR") {
  if (value === null || value === undefined || value === "") return "-";
  const amount = Number(value);
  if (Number.isNaN(amount)) return value;

  try {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${currency} ${amount}`;
  }
}

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
}

function getAllImagesFromEntity(entity) {
  const list = [];

  if (entity?.image_url) list.push(entity.image_url);
  if (entity?.imageUrl) list.push(entity.imageUrl);
  if (entity?.url) list.push(entity.url);

  if (Array.isArray(entity?.images)) {
    entity.images.forEach((img) => {
      if (typeof img === "string" && img) {
        list.push(img);
        return;
      }

      if (img?.url) list.push(img.url);
      if (img?.image_url) list.push(img.image_url);
      if (img?.imageUrl) list.push(img.imageUrl);
    });
  }

  return [...new Set(list.filter(Boolean))];
}

function getTimeValue(value) {
  if (!value) return 0;
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? 0 : time;
}

function getVariantLabel(variant = {}, index = 0) {
  return (
    variant?.label ||
    variant?.variantName ||
    variant?.name ||
    variant?.packLabel ||
    variant?.variantValue ||
    [variant?.weightValue, variant?.weightUnitSymbol].filter(Boolean).join(" ") ||
    `Variant ${index + 1}`
  );
}

function getPrimaryPrice(product) {
  const prices = Array.isArray(product?.prices) ? product.prices : [];
  const warehousePrices = prices.filter((p) => p?.warehouseId);

  const selectedPrice = warehousePrices.length
    ? [...warehousePrices].sort((a, b) => {
        const aActive = a?.active ? 1 : 0;
        const bActive = b?.active ? 1 : 0;
        if (bActive !== aActive) return bActive - aActive;

        const aFrom = getTimeValue(a?.effectiveFrom);
        const bFrom = getTimeValue(b?.effectiveFrom);
        return bFrom - aFrom;
      })[0]
    : prices.length
      ? [...prices].sort((a, b) => {
          const aActive = a?.active ? 1 : 0;
          const bActive = b?.active ? 1 : 0;
          if (bActive !== aActive) return bActive - aActive;

          const aFrom = getTimeValue(a?.effectiveFrom);
          const bFrom = getTimeValue(b?.effectiveFrom);
          return bFrom - aFrom;
        })[0]
      : null;

  return {
    id: selectedPrice?.id ?? "",
    mrp: selectedPrice?.mrp ?? product?.mrp ?? null,
    sellingPrice:
      selectedPrice?.sellingPrice ??
      selectedPrice?.selling_price ??
      product?.selling_price ??
      product?.sellingPrice ??
      null,
    currency: selectedPrice?.currency || "INR",
    discountPercent: selectedPrice?.discountPercent ?? null,
    active: selectedPrice?.active,
    effectiveFrom: selectedPrice?.effectiveFrom ?? null,
    effectiveTo: selectedPrice?.effectiveTo ?? null,
    warehouseId: selectedPrice?.warehouseId ?? null,
  };
}

function getAllVariantBatches(product) {
  const variants = Array.isArray(product?.variants) ? product.variants : [];

  return variants.flatMap((variant, index) => {
    const batches = Array.isArray(variant?.batches) ? variant.batches : [];

    return batches.map((batch) => ({
      ...batch,
      variantId: variant?.id,
      variantLabel: getVariantLabel(variant, index),
      variantSku: variant?.variantSku || variant?.sku || "",
      isDefault: Boolean(variant?.isDefault),
    }));
  });
}

function getStockSummary(product) {
  const batches = getAllVariantBatches(product);

  const validExpiryDates = batches
    .map((b) => b?.expiryDate)
    .filter(Boolean)
    .sort();

  return {
    totalReceived: batches.reduce((sum, b) => sum + Number(b?.qtyReceived || 0), 0),
    totalAvailable: batches.reduce((sum, b) => sum + Number(b?.qtyAvailable || 0), 0),
    totalReserved: batches.reduce((sum, b) => sum + Number(b?.qtyReserved || 0), 0),
    totalDamaged: batches.reduce((sum, b) => sum + Number(b?.qtyDamaged || 0), 0),
    nearestExpiry: validExpiryDates[0] || null,
    batchCount: batches.length,
  };
}

function Field({ label, value }) {
  return (
    <div className="rounded-[14px] border border-slate-200 bg-white p-4">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
        {label}
      </div>
      <div className="mt-2 break-words text-[14px] font-semibold text-slate-700">
        {valueOrDash(value)}
      </div>
    </div>
  );
}

function SectionCard({ title, children }) {
  return (
    <div
      className="rounded-[18px] border bg-white p-5 shadow-[0_12px_30px_rgba(216,106,120,0.06)]"
      style={{ borderColor: BORDER }}
    >
      <h3 className="text-[18px] font-bold text-[#d86a78]">{title}</h3>
      <div className="mt-4">{children}</div>
    </div>
  );
}

function VariantChip({ variant, active, onClick, index }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        "inline-flex items-center gap-2 rounded-full border px-3 py-2 text-[12px] font-semibold transition " +
        (active
          ? "border-[#d86a78] bg-[#fff0f3] text-[#d86a78]"
          : "border-slate-200 bg-white text-slate-600 hover:border-[#f2c6cc]")
      }
    >
      <span>{getVariantLabel(variant, index)}</span>
      {variant?.isDefault ? (
        <span className="rounded-full bg-[#ffdfe5] px-2 py-[2px] text-[10px] text-[#d86a78]">
          Default
        </span>
      ) : null}
    </button>
  );
}

export default function WarehouseProductDetailsPage({
  pageTitle = "PRODUCT DETAILS",
  backMode = "history",
  backTo = "/warehouse/dashboard/products",
}) {
  const navigate = useNavigate();
  const { productId, id } = useParams();
  const finalId = productId || id;

  const [loading, setLoading] = useState(true);
  const [product, setProduct] = useState(null);
  const [error, setError] = useState("");
  const [selectedVariantId, setSelectedVariantId] = useState("");
  const [selectedImage, setSelectedImage] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function loadDetails() {
      try {
        setLoading(true);
        setError("");

        const data = await warehouseCatalogService.getProductById(finalId);
        if (cancelled) return;

        const safeProduct = data || {};
        setProduct(safeProduct);
      } catch (err) {
        if (cancelled) return;
        setError(err?.message || "Failed to load product details");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    if (finalId) {
      loadDetails();
    } else {
      setLoading(false);
      setError("Product id is missing");
    }

    return () => {
      cancelled = true;
    };
  }, [finalId]);

  const variants = useMemo(
    () => (Array.isArray(product?.variants) ? product.variants : []),
    [product]
  );

  useEffect(() => {
    const first = variants.find((v) => v?.isDefault) || variants[0] || null;
    setSelectedVariantId(first?.id || "");
  }, [product?.id, variants]);

  const selectedVariant = useMemo(() => {
    return (
      variants.find((v) => String(v?.id) === String(selectedVariantId)) ||
      variants.find((v) => v?.isDefault) ||
      variants[0] ||
      null
    );
  }, [variants, selectedVariantId]);

  const parentImages = useMemo(() => getAllImagesFromEntity(product || {}), [product]);

  const galleryImages = useMemo(() => {
    const variantImages = getAllImagesFromEntity(selectedVariant || {});
    return variantImages.length ? variantImages : parentImages;
  }, [selectedVariant, parentImages]);

  useEffect(() => {
    const next = galleryImages[0] || "";
    setSelectedImage(next);
  }, [galleryImages, selectedVariantId]);

  const stock = useMemo(() => getStockSummary(product || {}), [product]);
  const price = useMemo(() => getPrimaryPrice(product || {}), [product]);
  const batches = useMemo(() => getAllVariantBatches(product || {}), [product]);

  if (loading) {
    return (
      <div className="rounded-[20px] border border-[#f2c6cc] bg-white p-6 text-slate-500">
        Loading product details...
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-[20px] border border-[#f2c6cc] bg-[#fff8f9] p-6 text-[#d86a78]">
        {error}
      </div>
    );
  }

  if (!product) {
    return (
      <div className="rounded-[20px] border border-[#f2c6cc] bg-white p-6 text-rose-600">
        Product details not found.
      </div>
    );
  }

  return (
    <>
      <div className="w-full">
        {previewOpen && selectedImage ? (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4 py-6"
            onClick={() => setPreviewOpen(false)}
          >
            <div
              className="relative max-h-[90vh] w-full max-w-[900px] rounded-[16px] bg-white p-3 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                onClick={() => setPreviewOpen(false)}
                className="absolute right-3 top-3 z-10 inline-flex h-9 w-9 items-center justify-center rounded-full bg-black/60 text-white hover:bg-black/75"
              >
                <X size={18} />
              </button>
              <div className="flex max-h-[82vh] items-center justify-center overflow-hidden rounded-[12px] bg-[#f8f8f8]">
                <img
                  src={selectedImage}
                  alt={product?.name || "product"}
                  className="max-h-[82vh] w-auto max-w-full object-contain"
                />
              </div>
            </div>
          </div>
        ) : null}

        <div className="mb-5 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => (backMode === "path" ? navigate(backTo) : navigate(-1))}
              className="inline-flex h-[40px] w-[40px] items-center justify-center rounded-full border border-[#f2c6cc] bg-white text-[#d86a78] hover:bg-[#fff4f6]"
            >
              <ArrowLeft size={18} />
            </button>
            <h1 className="text-[25px] font-bold tracking-wide text-[#d86a78]">
              {pageTitle}
            </h1>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-5 xl:grid-cols-[380px_minmax(0,1fr)]">
          <div className="rounded-[14px] bg-[#f8f8f8] p-4 sm:p-5">
            <div className="rounded-[14px] bg-white p-4 shadow-sm">
              <div className="overflow-hidden rounded-[12px] bg-[#f6f6f6]">
                {selectedImage ? (
                  <button
                    type="button"
                    onClick={() => setPreviewOpen(true)}
                    className="block w-full"
                  >
                    <img
                      src={selectedImage}
                      alt={product?.name || "product"}
                      className="h-[290px] w-full object-cover"
                    />
                  </button>
                ) : (
                  <div className="flex h-[290px] items-center justify-center text-[12px] text-slate-400">
                    <Package2 size={40} />
                  </div>
                )}
              </div>

              {galleryImages.length > 1 ? (
                <div className="mt-3 flex flex-wrap gap-2">
                  {galleryImages.map((img, index) => (
                    <button
                      key={`${img}-${index}`}
                      type="button"
                      onClick={() => setSelectedImage(img)}
                      className={
                        "overflow-hidden rounded-[10px] border p-[2px] " +
                        (selectedImage === img ? "border-[#d86a78]" : "border-slate-200")
                      }
                    >
                      <img
                        src={img}
                        alt={`thumb-${index}`}
                        className="h-[48px] w-[48px] object-cover"
                      />
                    </button>
                  ))}
                </div>
              ) : null}

              <div className="mt-5 grid grid-cols-3 gap-3 text-[12px]">
                <div>
                  <div className="text-[#d86a78]">Product Name</div>
                  <div className="mt-1 text-slate-600">{valueOrDash(product?.name)}</div>
                </div>
                <div>
                  <div className="text-[#d86a78]">SKU</div>
                  <div className="mt-1 text-slate-600">{valueOrDash(product?.sku)}</div>
                </div>
                <div>
                  <div className="text-[#d86a78]">Barcode</div>
                  <div className="mt-1 text-slate-600">{valueOrDash(product?.barcode)}</div>
                </div>
                <div>
                  <div className="text-[#d86a78]">Status</div>
                  <div className="mt-1 text-slate-600">
                    {valueOrDash(product?.status || (product?.is_active ? "active" : ""))}
                  </div>
                </div>
                <div>
                  <div className="text-[#d86a78]">Is Active</div>
                  <div className="mt-1 text-slate-600">
                    {product?.is_active === true
                      ? "true"
                      : product?.is_active === false
                        ? "false"
                        : "-"}
                  </div>
                </div>
                <div>
                  <div className="text-[#d86a78]">Slug</div>
                  <div className="mt-1 text-slate-600">{valueOrDash(product?.slug)}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-5">
            <SectionCard title="Product Information">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                <Field label="Category" value={product?.category_name || product?.categoryName} />
                <Field label="Brand" value={product?.brand_name || product?.brandName} />
                <Field
                  label="Unit"
                  value={product?.unit_symbol || product?.unit_name || product?.unitSymbol || product?.unitName}
                />
                <Field label="Tax Percent" value={product?.tax_percent || product?.taxPercent} />
              </div>

              <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                <Field
                  label="Short Description"
                  value={product?.short_description || product?.shortDescription}
                />
                <Field label="Description" value={product?.description} />
              </div>
            </SectionCard>

            <SectionCard title="Actual Product Variants">
              <div className="flex flex-wrap gap-2">
                {variants.map((variant, index) => (
                  <VariantChip
                    key={variant?.id || variant?.variantSku || variant?.variantName || index}
                    variant={variant}
                    index={index}
                    active={String(variant?.id) === String(selectedVariant?.id)}
                    onClick={() => setSelectedVariantId(variant?.id || "")}
                  />
                ))}
              </div>

              {selectedVariant ? (
                <div className="mt-5 rounded-[16px] border border-[#f2c6cc] bg-[#fffafb] p-4">
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                    <Field label="Variant Name" value={selectedVariant?.variantName || selectedVariant?.name} />
                    <Field label="Variant Group" value={selectedVariant?.variantGroup} />
                    <Field label="Variant Value" value={selectedVariant?.variantValue} />
                    <Field label="Pack Label" value={selectedVariant?.packLabel} />
                    <Field label="Variant SKU" value={selectedVariant?.variantSku || selectedVariant?.sku} />
                    <Field label="Variant Barcode" value={selectedVariant?.barcode} />
                    <Field
                      label="Qty / Pack"
                      value={[selectedVariant?.unitValue, selectedVariant?.unitSymbol].filter(Boolean).join(" ")}
                    />
                    <Field
                      label="Weight"
                      value={[selectedVariant?.weightValue, selectedVariant?.weightUnitSymbol].filter(Boolean).join(" ")}
                    />
                    <Field label="MRP" value={formatCurrency(selectedVariant?.mrp)} />
                    <Field label="Selling Price" value={formatCurrency(selectedVariant?.sellingPrice)} />
                    <Field label="Default" value={selectedVariant?.isDefault ? "Yes" : "No"} />
                    <Field
                      label="Active"
                      value={
                        selectedVariant?.isActive === true
                          ? "Yes"
                          : selectedVariant?.isActive === false
                            ? "No"
                            : "-"
                      }
                    />
                  </div>

                  <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                    <Field
                      label="Variant Short Description"
                      value={selectedVariant?.shortDescription || selectedVariant?.short_description}
                    />
                    <Field label="Variant Description" value={selectedVariant?.description} />
                  </div>
                </div>
              ) : (
                <div className="mt-5 rounded-[12px] border border-dashed border-slate-300 px-4 py-6 text-[12px] text-slate-400">
                  No variants found.
                </div>
              )}
            </SectionCard>

            <SectionCard title="Pricing Information">
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                <Field label="MRP" value={formatCurrency(price.mrp, price.currency)} />
                <Field label="Selling Price" value={formatCurrency(price.sellingPrice, price.currency)} />
                <Field
                  label="Discount Percent"
                  value={price.discountPercent ? `${price.discountPercent}%` : "-"}
                />
                <Field label="Currency" value={price.currency} />
                <Field
                  label="Price Active"
                  value={
                    typeof price.active === "boolean"
                      ? price.active
                        ? "true"
                        : "false"
                      : "-"
                  }
                />
                <Field label="Effective From" value={formatDateTime(price.effectiveFrom)} />
                <Field label="Effective To" value={formatDateTime(price.effectiveTo)} />
                <Field label="Warehouse Price Id" value={price.id || "-"} />
              </div>
            </SectionCard>

            <SectionCard title="Inventory Information">
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                <Field label="Available Qty" value={stock.totalAvailable} />
                <Field label="Received Qty" value={stock.totalReceived} />
                <Field label="Reserved Qty" value={stock.totalReserved} />
                <Field label="Damaged Qty" value={stock.totalDamaged} />
                <Field label="Batch Count" value={stock.batchCount} />
                <Field label="Nearest Expiry" value={formatDate(stock.nearestExpiry)} />
              </div>
            </SectionCard>

            <SectionCard title="Variant Stock Information">
              {variants.length ? (
                <div className="space-y-4">
                  {variants.map((variant, index) => {
                    const variantBatches = Array.isArray(variant?.batches) ? variant.batches : [];

                    const variantSummary = {
                      totalReceived: variantBatches.reduce(
                        (sum, b) => sum + Number(b?.qtyReceived || 0),
                        0
                      ),
                      totalAvailable: variantBatches.reduce(
                        (sum, b) => sum + Number(b?.qtyAvailable || 0),
                        0
                      ),
                      totalReserved: variantBatches.reduce(
                        (sum, b) => sum + Number(b?.qtyReserved || 0),
                        0
                      ),
                      totalDamaged: variantBatches.reduce(
                        (sum, b) => sum + Number(b?.qtyDamaged || 0),
                        0
                      ),
                      nearestExpiry:
                        variantBatches
                          .map((b) => b?.expiryDate)
                          .filter(Boolean)
                          .sort()?.[0] || null,
                      batchCount: variantBatches.length,
                    };

                    return (
                      <div
                        key={variant?.id || index}
                        className="rounded-[18px] border border-[#f3d5da] bg-[#fffafb] p-4"
                      >
                        <div className="mb-4 flex flex-wrap items-center gap-2">
                          <div className="text-[15px] font-bold text-slate-800">
                            {getVariantLabel(variant, index)}
                          </div>

                          {variant?.isDefault ? (
                            <span className="rounded-full bg-[#fdecef] px-2.5 py-1 text-[11px] font-semibold text-[#d86a78]">
                              Default
                            </span>
                          ) : null}

                          <span className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-medium text-slate-600">
                            SKU: {valueOrDash(variant?.variantSku || variant?.sku)}
                          </span>
                        </div>

                        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                          <Field label="Available Qty" value={variantSummary.totalAvailable} />
                          <Field label="Received Qty" value={variantSummary.totalReceived} />
                          <Field label="Reserved Qty" value={variantSummary.totalReserved} />
                          <Field label="Damaged Qty" value={variantSummary.totalDamaged} />
                          <Field label="Batch Count" value={variantSummary.batchCount} />
                          <Field label="Nearest Expiry" value={formatDate(variantSummary.nearestExpiry)} />
                          <Field label="MRP" value={formatCurrency(variant?.mrp, "INR")} />
                          <Field label="Selling Price" value={formatCurrency(variant?.sellingPrice, "INR")} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="rounded-[16px] border border-dashed border-[#f2c6cc] bg-[#fffafb] px-4 py-8 text-center text-[14px] text-slate-500">
                  No variants available
                </div>
              )}
            </SectionCard>

            <SectionCard title="System Information">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <Field label="Created At" value={formatDateTime(product?.created_at || product?.createdAt)} />
                <Field label="Updated At" value={formatDateTime(product?.updated_at || product?.updatedAt)} />
              </div>
            </SectionCard>

            <SectionCard title="Batch Details">
              {batches.length ? (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[900px]">
                    <thead className="bg-[#fff7f8]">
                      <tr className="text-left text-[12px] font-semibold uppercase tracking-[0.04em] text-slate-500">
                        <th className="rounded-l-[12px] px-4 py-3">Variant</th>
                        <th className="px-4 py-3">Batch No</th>
                        <th className="px-4 py-3">UOM</th>
                        <th className="px-4 py-3">MFG Date</th>
                        <th className="px-4 py-3">Expiry Date</th>
                        <th className="px-4 py-3">Received</th>
                        <th className="px-4 py-3">Available</th>
                        <th className="px-4 py-3">Reserved</th>
                        <th className="rounded-r-[12px] px-4 py-3">Damaged</th>
                      </tr>
                    </thead>
                    <tbody>
                      {batches.map((batch) => (
                        <tr
                          key={batch?.id || `${batch?.variantId}-${batch?.batchNo}`}
                          className="border-b border-[#f5e3e6] text-[14px] text-slate-700 last:border-b-0"
                        >
                          <td className="px-4 py-3 font-semibold">{valueOrDash(batch?.variantLabel)}</td>
                          <td className="px-4 py-3 font-semibold">{valueOrDash(batch?.batchNo)}</td>
                          <td className="px-4 py-3">{valueOrDash(batch?.uom)}</td>
                          <td className="px-4 py-3">{formatDate(batch?.mfgDate)}</td>
                          <td className="px-4 py-3">{formatDate(batch?.expiryDate)}</td>
                          <td className="px-4 py-3">{Number(batch?.qtyReceived || 0)}</td>
                          <td className="px-4 py-3">{Number(batch?.qtyAvailable || 0)}</td>
                          <td className="px-4 py-3">{Number(batch?.qtyReserved || 0)}</td>
                          <td className="px-4 py-3">{Number(batch?.qtyDamaged || 0)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="rounded-[16px] border border-dashed border-[#f2c6cc] bg-[#fffafb] px-4 py-8 text-center text-[14px] text-slate-500">
                  No batch details available
                </div>
              )}
            </SectionCard>
          </div>
        </div>
      </div>
    </>
  );
}